export const ENET_ASSET_LIST_KIND = {
  ALL: 'ALL',
  HOST: 'HOST',
  DOMAIN: 'DOMAIN',
  GATEWAY: 'GATEWAY',
  NETWORK_SERVICE: 'NETWORK_SERVICE',
  SOFTWARE: 'SOFTWARE',
  STORAGE_SERVICE: 'STORAGE_SERVICE',
  NETBLOCK: 'NETBLOCK',
} as const;

export type EnetAssetListKind =
  (typeof ENET_ASSET_LIST_KIND)[keyof typeof ENET_ASSET_LIST_KIND];

export const ENET_ASSET_LIST_COLUMN = {
  assetType: 'assetType',
  attribution: 'attribution',
  signals: 'signals',
  findings: 'findings',
  scope: 'scope',
  source: 'source',
  ip: 'ip',
  registrant: 'registrant',
  country: 'country',
  hosts: 'hosts',
  level: 'level',
  subdomains: 'subdomains',
  dnsRecords: 'dnsRecords',
  resolvesTo: 'resolvesTo',
  registrar: 'registrar',
  reverseDns: 'reverseDns',
  netblock: 'netblock',
  provider: 'provider',
  function: 'function',
  domains: 'domains',
  services: 'services',
  transport: 'transport',
  host: 'host',
  tls: 'tls',
  vendor: 'vendor',
  domain: 'domain',
  category: 'category',
  detection: 'detection',
  access: 'access',
} as const;

export type EnetAssetListColumnId =
  (typeof ENET_ASSET_LIST_COLUMN)[keyof typeof ENET_ASSET_LIST_COLUMN];

export const ENET_ASSET_LIST_COLUMN_WIDTHS: Record<
  EnetAssetListColumnId,
  string
> = {
  assetType: '100px',
  attribution: '80px',
  signals: '78px',
  findings: '78px',
  scope: '88px',
  source: '100px',
  ip: '100px',
  registrant: '120px',
  country: '64px',
  hosts: '64px',
  level: '88px',
  subdomains: '72px',
  dnsRecords: '56px',
  resolvesTo: '120px',
  registrar: '100px',
  reverseDns: '120px',
  netblock: '110px',
  provider: '80px',
  function: '88px',
  domains: '64px',
  services: '64px',
  transport: '56px',
  host: '110px',
  tls: '56px',
  vendor: '88px',
  domain: '140px',
  category: '100px',
  detection: '100px',
  access: '80px',
};

export const ENET_ASSET_LIST_COLUMNS: Record<
  EnetAssetListKind,
  readonly EnetAssetListColumnId[]
> = {
  ALL: ['assetType', 'attribution', 'signals', 'findings', 'scope', 'source'],
  NETBLOCK: ['ip', 'registrant', 'country', 'hosts', 'scope', 'source'],
  DOMAIN: [
    'level',
    'subdomains',
    'dnsRecords',
    'resolvesTo',
    'registrar',
    'signals',
    'findings',
    'scope',
    'source',
  ],
  HOST: ['reverseDns', 'netblock', 'provider', 'signals', 'findings', 'source'],
  GATEWAY: ['provider', 'function', 'domains', 'services', 'scope', 'source'],
  NETWORK_SERVICE: [
    'transport',
    'host',
    'tls',
    'signals',
    'findings',
    'scope',
    'source',
  ],
  SOFTWARE: [
    'domain',
    'vendor',
    'category',
    'signals',
    'findings',
    'detection',
  ],
  STORAGE_SERVICE: [
    'provider',
    'access',
    'signals',
    'findings',
    'scope',
    'source',
  ],
};

export const ENET_ASSET_LIST_NAME_COLUMN_WIDTH = '14rem';

const ENET_ASSET_LIST_COLUMN_GAP_PX = 12;
const ENET_ASSET_LIST_NAME_COLUMN_PX = 224;
const ENET_ASSET_LIST_ROW_PADDING_PX = 48;

const enetAssetListRowMinWidthCss = (listKind: EnetAssetListKind): string => {
  const columns = ENET_ASSET_LIST_COLUMNS[listKind];
  const metricsPx = columns.reduce(
    (sum, column) =>
      sum + Number.parseInt(ENET_ASSET_LIST_COLUMN_WIDTHS[column], 10),
    0,
  );
  const gapPx = Math.max(columns.length - 1, 0) * ENET_ASSET_LIST_COLUMN_GAP_PX;

  return `${ENET_ASSET_LIST_NAME_COLUMN_PX + metricsPx + gapPx + ENET_ASSET_LIST_ROW_PADDING_PX}px`;
};

const ENET_ASSET_LIST_KINDS = Object.values(
  ENET_ASSET_LIST_KIND,
) as EnetAssetListKind[];

export const ENET_ASSET_LIST_ROW_MIN_WIDTH = Object.fromEntries(
  ENET_ASSET_LIST_KINDS.map((listKind) => [
    listKind,
    enetAssetListRowMinWidthCss(listKind),
  ]),
) as Record<EnetAssetListKind, string>;

export const ENET_ASSET_LIST_GRID_TEMPLATE = Object.fromEntries(
  ENET_ASSET_LIST_KINDS.map((listKind) => [
    listKind,
    ENET_ASSET_LIST_COLUMNS[listKind]
      .map((column) => ENET_ASSET_LIST_COLUMN_WIDTHS[column])
      .join(' '),
  ]),
) as Record<EnetAssetListKind, string>;

export function enetAssetListGridTemplate({
  listKind,
}: {
  listKind: EnetAssetListKind;
}): string {
  return ENET_ASSET_LIST_GRID_TEMPLATE[listKind];
}

export function enetAssetListHasDetectionColumns({
  listKind,
}: {
  listKind: EnetAssetListKind;
}): boolean {
  return ENET_ASSET_LIST_COLUMNS[listKind].some(
    (column) =>
      column === ENET_ASSET_LIST_COLUMN.findings ||
      column === ENET_ASSET_LIST_COLUMN.signals,
  );
}
